<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import config from '@/config.json';

const props = defineProps<{
  overlayIsVisible: boolean,
}>();

const emit = defineEmits<{
  (e: 'setIsLoading', value: boolean): void,
  (e: 'toggleOverlay', value: boolean): void,
}>();

const checkboxRef = ref(null);

watch (props, (value) => {
  if (!value.overlayIsVisible && checkboxRef.value) {
    (checkboxRef.value as HTMLElement).focus();
  }
});

const email = ref('');
const checked = ref(false);
const submitted = ref(false);

const isValid = () => {
  const regEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return email.value && email.value.match(regEx) && checked.value;
};

const onSubmit = async () => {
  const toast = useToast();

  submitted.value = true;

  if (!isValid()) {
    toast.error('Bitte gib eine gültige E-Mail-Adresse ein und akzeptiere die Bedingungen zum Datenschutz.', {
      id: 'notValid',
    });

    return;
  }

  emit('setIsLoading', true);

  toast.dismiss('notValid');

  await sendEmail(email.value);

  onReset();
  emit('setIsLoading', false);
};

const sendEmail = async (address: string) => {
  const toast = useToast();

  if (!config.sendEmail) {
    toast.success('Vielen Dank, du hörst demnächst von uns! 🙂', {
      id: 'isValid',
    });

    return;
  }

  const requestOptions = {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      ctx: 'quiz',
      email: address,
      to: config.sendEmailTo
    }),
  };

  try {
    await fetch(config.sendEmailUrl, requestOptions)
        .then(response => response.json())
        .then(response => {
          if (response === 'success') {
            toast.success('Vielen Dank, du hörst demnächst von uns! 🙂', {
              id: 'isValid',
            });
          } else {
            toast.error('Oops, da ist wohl etwas schief gelaufen. Bitte versuche es später noch einmal.', {
              id: 'error',
            });
          }
        });
  } catch (exception) {
    console.error(exception);
    toast.error('Oops, da ist wohl etwas schief gelaufen. Bitte versuche es später noch einmal.', {
      id: 'error',
    });
  }
};

const onReset = () => {
  email.value = '';
  checked.value = false;
  submitted.value = false;
};

const onToggleCheckbox = () => {
  checked.value = !checked.value;
};

const onToggleOverlay = () => {
  emit('toggleOverlay', true);
};
</script>


<template>
  <section class="contact" aria-label="Kontaktmöglichkeit">
    <form autocomplete="off" @submit.prevent="onSubmit">
      <div class="icon">
        <img src="@/assets/img/spacecraft.svg" alt="" />
      </div>
      <h2>Fragen zum Projekt?</h2>
      <p>Hinterlasse uns gerne deine E-Mail-Adresse. Wir melden uns dann bei dir.</p>
      <input
          class="email"
          v-model="email" placeholder="Deine E-Mail-Adresse..."
      />
      <p class="data-privacy">
        <span>Ich akzeptiere die Bestimmungen zum
          <a
            v-on:click.stop="onToggleOverlay()"
            v-on:keyup.enter.space="onToggleOverlay()"
            role="button"
            tabIndex="0"
          >
            Datenschutz</a>.
        </span>
        <label for="accept-terms-checkbox">
          <input
              ref="checkboxRef"
              id="accept-terms-checkbox"
              type="checkbox"
              value="1"
              :checked="checked"
              v-on:click.stop="onToggleCheckbox"
          />
        </label>
      </p>
      <button type="submit">Abschicken</button>
      <a
          class="reset"
          v-if="email"
          v-on:click="onReset"
          v-on:keyup.enter.space="onReset"
          role="button"
          tabIndex="0"
      >
        &times; Löschen
      </a>
    </form>
  </section>
</template>


<style lang="scss" scoped>
@import '@/assets/scss/colors.scss';
@import '@/assets/scss/layout.scss';
@import '@/assets/scss/typography.scss';
@import '@/assets/scss/buttons.scss';

.contact {
  form {
    position: relative;
    display: block;
    border: 1px solid $color-gray-400;
    padding: 0.5em 2.25em 1em;
    background: $color-light;

    h2 {
      font-size: $font-size-large;
      margin-top: 1em;
      margin-bottom: 0.5em;
    }

    .icon {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      background: $color-light;
      top: 1.5em;
      right: 1.5em;

      img {
        width: 3em;
        height: 3em;
      }
    }

    p {
      margin-bottom: 1em;
    }

    input.email {
      border-radius: 1.5em;
      border-color: $color-primary-400;
      background: $color-gray-100;

      &:focus {
        background: $color-primary-100;

        &::placeholder {
          color: $color-secondary-400;
        }
      }
    }

    input[type=checkbox] {
      margin-right: 0.5em;
      padding: inherit;
      cursor: pointer;
    }

    .reset {
      font-weight: $font-weight-medium;
      margin-left: 1em;
      cursor: pointer;
      color: $color-secondary-lightest;
    }

    button:active + a {
      position: relative;
      top: -3px;
    }

    a {
      text-decoration-color: $color-primary-400;
    }

    .data-privacy {
      display: flex;
      flex-direction: row-reverse;
      justify-content: flex-end;
      align-items: center;
      margin-top: 0.5em;
      padding: 0.5em 0;
      font-weight: $font-weight-medium;

      a {
        text-decoration: underline;
        text-decoration-color: $color-primary-400;
      }
    }
  }

  img {
    max-width: 100%;
    width: 920px;
    margin: 0 auto;
    display: block;
  }

  label {
    display: flex;
    justify-content: center;
  }
}
</style>
